import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ClassmanagementService } from '../classmanagement.service';
import { ModulemanagementService } from '../modules/modulemanagement.service';
import { UtilService } from '../util.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineCryptoService } from './offline-crypto.service';
import { OfflineSessionService } from './offline-session.service';
import { OfflineEntitlementService } from './offline-entitlement.service';
import { buildContentKey, buildIndexKey, getCurrentSchoolSubdomain, getCurrentUserId } from './offline-cache-key';
import { canSaveForOffline } from './offline-feature-gate';
import { IndexRecord } from './offline-db.types';
import { LessonGamePlay } from 'src/app/models/module/lessongame.model';
import { ensurePersistence } from './offline-quota';
import { OfflineContentReaderService } from './offline-content-reader.service';
import { LessonVideosResult, OfflineMediaService } from './offline-media.service';
import { extractNoteVideoRefs } from './offline-note-videos';
import { UseraccessserviceService } from '../useraccessservice.service';

// A version for a lesson's saved games that changes only when a game really changes. The API
// shuffles terms, definitions and steps on every request, so those are left out; the ids,
// titles, cards and answer key are stable and cover every edit a teacher can make.
function gamesVersion(games: LessonGamePlay[] | null | undefined): string {
  const stable = JSON.stringify(
    (games ?? [])
      .map((g) => ({ id: g.id, page: g.page, type: g.gameType, title: g.title, cards: g.cards ?? [], answers: g.answers ?? null }))
      .sort((a, b) => a.id - b.id),
  );
  let hash = 5381;
  for (let i = 0; i < stable.length; i++) hash = ((hash << 5) + hash + stable.charCodeAt(i)) | 0;
  return `g${(hash >>> 0).toString(36)}-${stable.length}`;
}

export type SaveResult = { ok: true } | { ok: false; error: string };
type SaveGuardResult = { ok: true; school: string; user: number } | { ok: false; error: string };

interface SaveUnitSpec {
  kind: string;
  resourceId: string;
  kindGroup: IndexRecord['kindGroup'];
  fetch: () => Promise<unknown>;
  extractVersion?: (body: unknown) => string | undefined;
}

// Offline mode's save orchestrator (docs/offline-mode-plan.md P3). Writes directly to
// IndexedDB itself — there is no interceptor in this app that caches arbitrary page traffic
// anymore (that whole approach was scrapped: offline access is confined to the downloads hub,
// which reads only what was explicitly saved here, never through HttpClient).
@Injectable({
  providedIn: 'root',
})
export class OfflineSaveService {
  savedState$ = new BehaviorSubject<Map<string, IndexRecord>>(new Map());

  constructor(
    private classmanager: ClassmanagementService,
    private modulemanager: ModulemanagementService,
    private util: UtilService,
    private useraccessservice: UseraccessserviceService,
    private offlineDb: OfflineDbService,
    private offlineCrypto: OfflineCryptoService,
    private offlineSession: OfflineSessionService,
    private offlineEntitlement: OfflineEntitlementService,
    private toastr: ToastrService,
    private contentReader: OfflineContentReaderService,
    private offlineMedia: OfflineMediaService,
  ) {}

  async saveLesson(moduleId: number, classId: number, opts: { includeFiles?: boolean } = {}): Promise<SaveResult> {
    const guard = await this.guardCanSave(moduleId);
    if (!guard.ok) return guard;

    const { school, user } = guard;
    const classIdStr = String(classId);
    const moduleIdStr = String(moduleId);

    const units: SaveUnitSpec[] = [
      {
        kind: 'lesson',
        resourceId: classIdStr,
        kindGroup: 'text',
        fetch: () => this.classmanager.getClassDetails(classId).toPromise(),
        extractVersion: (body: any) => body?.lesson?.modifiedDateTime,
      },
      {
        kind: 'note',
        resourceId: classIdStr,
        kindGroup: 'text',
        fetch: () => this.classmanager.getClassNote(classId).toPromise(),
        extractVersion: (body: any) =>
          Array.isArray(body) && body.length
            ? body.reduce((latest: string, n: any) => (n?.modifiedDateTime > latest ? n.modifiedDateTime : latest), body[0]?.modifiedDateTime ?? '')
            : undefined,
      },
      // The downloads hub's source of this course's title and full lesson list (its own
      // data.lessons[]) — the one nav-shell fetch still worth caching now that offline access
      // is confined to that hub rather than trying to keep arbitrary pages alive offline.
      { kind: 'navshell-moduleview', resourceId: moduleIdStr, kindGroup: 'text', fetch: () => this.modulemanager.getModuleDetails(moduleId).toPromise() },
      // The lesson's games WITH their answer keys, so the offline reader can show them and check
      // attempts on the device. Tolerates a 404 (an API deployed before lessongamesoffline
      // existed) as "no games", so that ordering of deploys can never break lesson saving.
      {
        kind: 'lessongames',
        resourceId: classIdStr,
        kindGroup: 'text',
        fetch: () => this.fetchGamesForOffline(classId),
        extractVersion: (body: any) => gamesVersion(body),
      },
    ];
    if (opts.includeFiles) {
      units.push({ kind: 'lessonfiles', resourceId: classIdStr, kindGroup: 'files', fetch: () => this.classmanager.getclassfiles(classId).toPromise() });
    }

    return this.saveUnits(school, user, moduleId, units);
  }

  // The videos embedded in a lesson's notes, saved after the notes (call saveLesson first).
  //
  // Deliberately NOT part of saveLesson: revalidateVersions() re-runs saveLesson on every
  // reconnect, and a video is far too large to re-download silently in the background - that
  // is the student's data allowance. This runs only from the student's own tap.
  //
  // Reads the note from the copy just saved on the device, so the videos saved are exactly the
  // ones the offline reader will look for. Null if saving is not allowed here.
  async saveLessonVideos(moduleId: number, classId: number, onProgress?: (videoIndex: number, videoCount: number, percent: number) => void): Promise<LessonVideosResult | null> {
    const guard = await this.guardCanSave(moduleId);
    if (!guard.ok) return null;
    const notes = await this.contentReader.read<any[]>(guard.school, guard.user, 'note', String(classId));
    const refs = extractNoteVideoRefs(notes);
    return this.offlineMedia.saveLessonVideos(refs, guard.school, guard.user, onProgress);
  }

  // Whether the saved copy of a lesson can actually be read back. A lesson can be recorded as
  // saved yet be unreadable - encrypted with a device key that was later lost (see
  // OfflineDbService.claimDeviceKeyRecord) - and such a lesson must offer Save again instead
  // of sitting greyed out as "Saved offline" with nothing behind it.
  async isLessonReadable(classId: number): Promise<boolean> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return false;
    return (await this.contentReader.read(school, user, 'lesson', String(classId)).catch(() => null)) !== null;
  }

  // Whether a saved lesson's videos are all on the device too - the lesson only shows "Saved
  // offline" when they are. True for a lesson with no videos.
  async lessonVideosSaved(classId: number): Promise<boolean> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return false;
    const notes = await this.contentReader.read<any[]>(school, user, 'note', String(classId));
    return this.offlineMedia.allLessonVideosSaved(extractNoteVideoRefs(notes), school, user);
  }

  async saveNote(moduleId: number, classId: number): Promise<SaveResult> {
    // Notes for a lesson are all fetched and cached together under the 'note' kind (the API
    // returns every page in one call) — saving "a note" really means ensuring the lesson's
    // note set and its ancestor navigation are saved, same as saveLesson minus the body text
    // duplication. Kept as its own method so P3's per-note save control has a natural target
    // that doesn't also imply "and download every attachment."
    return this.saveLesson(moduleId, classId, { includeFiles: false });
  }

  // Bundled removal for the downloads hub's lesson-level "remove" action — lesson, its notes,
  // and any files are one saved unit from the student's point of view (saveLesson/saveNote
  // always save them together), so unsave must be atomic across all three. The old per-kind
  // unsave() below is kept only for evictLru/removeAll's own bookkeeping, not for UI use —
  // calling it directly from the hub could orphan a note without its lesson or vice versa.
  async unsaveLesson(classId: string): Promise<void> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return;
    for (const kind of ['lesson', 'note', 'lessonfiles', 'lessongames']) {
      const key = buildIndexKey(school, user, kind, classId);
      const entry = await this.offlineDb.getIndexEntry(key);
      if (entry) await this.deleteUnit(school, user, entry);
    }
    this.refreshSavedStateSnapshot(school, user);
  }

  async unsave(kind: string, resourceId: string): Promise<void> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return;
    await this.deleteUnit(school, user, { key: buildIndexKey(school, user, kind, resourceId), kind, resourceId } as IndexRecord);
    this.refreshSavedStateSnapshot(school, user);
  }

  private async deleteUnit(school: string, user: number, entry: IndexRecord): Promise<void> {
    const contentKey = buildContentKey(school, user, entry.kind, entry.resourceId, entry.currentVersion ?? 'unversioned');
    await this.offlineDb.deleteContent(contentKey).catch(() => undefined);
    await this.offlineDb.deleteIndexEntry(entry.key);
  }

  // P6 — oldest-savedAt-first, until there's room for bytesNeeded. User-confirmed (never
  // silent) via the same confirm() dialog the rest of the app uses, naming exactly what will
  // be removed. Never evicts the unit named in `excludeKey` (the one currently being viewed/
  // just downloaded).
  async evictLru(bytesNeeded: number, excludeKey?: string): Promise<boolean> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return false;

    const entries = (await this.offlineDb.listIndexEntries(school, user))
      .filter((e) => e.key !== excludeKey)
      .sort((a, b) => a.savedAt - b.savedAt);

    let freed = 0;
    const toRemove: IndexRecord[] = [];
    for (const entry of entries) {
      if (freed >= bytesNeeded) break;
      toRemove.push(entry);
      freed += entry.sizeBytes;
    }
    if (toRemove.length === 0) return false;

    const names = toRemove.map((e) => `${e.kind} ${e.resourceId}`).join(', ');
    const confirmed = await this.util.confirm('Free up space?', `Remove these saved items to make room: ${names}?`);
    if (!confirmed) return false;

    for (const entry of toRemove) {
      await this.deleteUnit(school, user, entry);
    }
    this.refreshSavedStateSnapshot(school, user);
    return true;
  }

  // Called on the connectivity service's ->online transition (offline-connectivity.service.ts
  // consumers). Compares each cached lesson/note's server-set ModifiedDateTime against the
  // live value and refreshes anything stale — plan §6 P3's "content-version check on
  // reconnect." Nav-shell rows stay 'unversioned' (no version column) and are simply
  // cheap to refetch unconditionally alongside their lesson/note sibling when it's stale.
  async revalidateVersions(): Promise<void> {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return;

    const entries = await this.offlineDb.listIndexEntries(school, user);
    const staleClassIds = new Set<number>();
    for (const entry of entries) {
      if (entry.kind !== 'lesson' && entry.kind !== 'note' && entry.kind !== 'lessongames') continue;
      try {
        let liveVersion: string | undefined;
        if (entry.kind === 'lessongames') {
          liveVersion = gamesVersion(await this.fetchGamesForOffline(Number(entry.resourceId)));
        } else {
          const live: any =
            entry.kind === 'lesson'
              ? await this.classmanager.getClassDetails(Number(entry.resourceId)).toPromise()
              : await this.classmanager.getClassNote(Number(entry.resourceId)).toPromise();
          liveVersion =
            entry.kind === 'lesson'
              ? live?.lesson?.modifiedDateTime
              : Array.isArray(live) && live.length
              ? live.reduce((latest: string, n: any) => (n?.modifiedDateTime > latest ? n.modifiedDateTime : latest), live[0]?.modifiedDateTime ?? '')
              : undefined;
        }
        if (liveVersion && liveVersion !== entry.currentVersion) {
          staleClassIds.add(Number(entry.resourceId));
        }
      } catch (err) {
        console.error('[offline] version check failed', entry.key, err);
      }
    }
    for (const classId of staleClassIds) {
      const entries2 = await this.offlineDb.listIndexEntries(school, user);
      const owner = entries2.find((e) => e.resourceId === String(classId) && e.kind === 'lesson');
      if (owner) await this.saveLesson(owner.moduleId, classId);
    }
  }

  // The write path — encrypts and stores each unit's response directly. No interceptor
  // involved: this app no longer tries to serve arbitrary pages from cache, so there is
  // nothing to intercept a request into doing this as a side effect anymore.
  private async saveUnits(school: string, user: number, moduleId: number, units: SaveUnitSpec[]): Promise<SaveResult> {
    const now = Date.now();
    const keys: string[] = [];
    try {
      for (const unit of units) {
        const key = buildIndexKey(school, user, unit.kind, unit.resourceId);
        keys.push(key);
        await this.offlineDb.markSaved({
          key,
          school,
          user,
          kind: unit.kind,
          resourceId: unit.resourceId,
          moduleId,
          kindGroup: unit.kindGroup,
          role: 'student',
          status: 'saving',
          currentVersion: 'pending',
          sizeBytes: 0,
          savedAt: now,
        });
      }

      const deviceKey = await this.offlineCrypto.getOrCreateDeviceKey();
      for (const unit of units) {
        const indexKey = buildIndexKey(school, user, unit.kind, unit.resourceId);
        // What this unit was stored under BEFORE this save. Content is keyed by version, so a
        // re-save at a new version writes a new row and leaves the old one behind, unreachable
        // but still occupying the student's storage quota. revalidateVersions() re-saves every
        // changed lesson on every reconnect, so that leaked once per edit, forever.
        const previousVersion = (await this.offlineDb.getIndexEntry(indexKey))?.currentVersion;

        const body = await unit.fetch();
        const version = unit.extractVersion?.(body) ?? 'unversioned';
        const plaintext = new TextEncoder().encode(JSON.stringify(body)).buffer;
        const { iv, ciphertext } = await this.offlineCrypto.encryptBlob(deviceKey, plaintext);
        const contentKey = buildContentKey(school, user, unit.kind, unit.resourceId, version);

        await this.offlineDb.putContent({
          key: contentKey,
          school,
          user,
          kind: unit.kind,
          resourceId: unit.resourceId,
          version,
          iv,
          ciphertext,
          savedAt: Date.now(),
          bytes: plaintext.byteLength,
        });
        // Only once the replacement is safely written, and never when the version is
        // unchanged (that key is the row we just wrote).
        if (previousVersion && previousVersion !== 'pending' && previousVersion !== version) {
          await this.offlineDb
            .deleteContent(buildContentKey(school, user, unit.kind, unit.resourceId, previousVersion))
            .catch((err) => console.error('[offline] could not remove superseded content', err));
        }
        await this.offlineDb.touchIndex(indexKey, version, plaintext.byteLength);
      }

      this.refreshSavedStateSnapshot(school, user);
      // Ask the browser not to evict downloads under storage pressure. Fire-and-forget: a
      // refusal changes nothing about this save.
      ensurePersistence().catch(() => undefined);
      return { ok: true };
    } catch (err) {
      console.error('[offline] save failed', err);
      // Never leave a half-saved unit reporting as usable offline.
      for (const key of keys) {
        await this.offlineDb.deleteIndexEntry(key);
      }
      this.toastr.error('Could not save this lesson for offline use. Please try again.', 'Offline save failed');
      this.refreshSavedStateSnapshot(school, user);
      return { ok: false, error: (err as Error)?.message ?? 'unknown error' };
    }
  }

  private async guardCanSave(moduleId: number): Promise<SaveGuardResult> {
    const schoolToggle = this.offlineSession.current?.offlineEnabled ?? false;
    if (!canSaveForOffline(schoolToggle)) {
      // Either the school has offline mode off, or this is an ordinary browser tab rather
      // than the installed app. Reading back what is already saved is NOT gated this way —
      // see offline-feature-gate.ts for why only saving is.
      return { ok: false, error: 'offline saving is not available here' };
    }
    if (this.offlineSession.current?.isAdmin) {
      return { ok: false, error: 'offline saving is not available for admins' }; // D5
    }
    const entitlement = await this.offlineEntitlement.getValid();
    if (!entitlement || !entitlement.moduleIds.includes(moduleId)) {
      return { ok: false, error: 'no valid offline entitlement for this module' };
    }
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return { ok: false, error: 'not signed in' };
    return { ok: true, school, user };
  }

  private fetchGamesForOffline(classId: number): Promise<LessonGamePlay[]> {
    return this.classmanager
      .getLessonGamesOffline(classId)
      .toPromise()
      .then((games) => games ?? [])
      .catch((err) => {
        if (err?.status === 404) return [];
        throw err;
      });
  }

  private refreshSavedStateSnapshot(school: string, user: number): void {
    this.offlineDb
      .listIndexEntries(school, user)
      .then((entries) => {
        const map = new Map<string, IndexRecord>();
        for (const entry of entries) map.set(entry.key, entry);
        this.savedState$.next(map);
      })
      .catch((err) => console.error('[offline] failed to refresh saved-state snapshot', err));
  }
}
